import { Action } from 'redux'
import { combineEpics, Epic, ofType } from 'redux-observable'
import {
  catchError,
  EMPTY,
  from,
  map,
  mergeMap,
  of,
  switchMap,
  takeUntil,
  toArray,
  withLatestFrom
} from 'rxjs'
import {
  compareBestFirst,
  isPending
} from '../../containers/TradingList/helpers'
import { getHub } from '../../helpers/hub'
import { createInvokeEpic } from '../../signalr/createInvokeEpic'
import { createStreamEpic } from '../../signalr/createStreamEpic'
import { addLogItem } from '../log/actions'
import {
  AddOrUpdateUserOrdersAction,
  cancelOrders,
  submitOrder,
  SubmitOrderAction
} from '../order/actions'
import { createOrderFromResponse } from '../order/helpers'
import { getListTradeOrders } from '../order/selectors'
import { Order, OrderResponse } from '../order/types'
import { getUserPreferences } from '../userPreferences/selectors'
import {
  getEnableLTHedge,
  getListTradingMaxRows
} from '../webSettings/selectors'
import { logInvalidIdentifiers } from '../upload/actions'
import { logError } from '../ws/actions'
import {
  addListTradingRows,
  CancelListTradingOrdersAction,
  createListTradingListFailure,
  CreateWorkingListTradingOrderAction,
  DeleteListTradingSecuritiesAction,
  listTradingSecuritiesFetch,
  listTradingSecuritiesFetchFail,
  listTradingSecuritiesFetchSuccess,
  ListTradingSecuritiesFetchAction,
  SaveNewListTradingRowsAction,
  validateIdentifiersComplete,
  validateIdentifiersFailed,
  ValidateListTradingIdentifiersAction,
  toggleCancelSubOrderSelection,
  ToggleCancelSubOrderSelectionAction,
  TradeSelectedSecuritiesAction,
  unwatchOrder,
  updateListTradingList,
  watchOrder
} from './actions'
import {
  addNewListTradingRowsDefinition,
  createListTradingListDefinition,
  deleteListTradingSecurityDefinition,
  updateListTradingListDefinition,
  watchOrderDefinition
} from './definition'
import {
  getSelectedSecurities,
  getWatchedOrderIds,
  getWatchedOrders,
  getWorkingOrderFromStateBySecurityId,
  getWorkingOrders
} from './selectors'
import { ListTradingSecurity } from './types'
import { fetchListTradingSecurities } from '../securities/actions'
import { getSecurityIdsFromIdentifier$ } from '../upload/helpers'
import { getWatchlistDetails } from '../watchList/selectors'
import { allResolved, buildResolutions } from './helpers'
import { State } from '../types'

type ListTradingSecurityResponse = Omit<ListTradingSecurity, 'bestOrder'> & {
  bestOrder: OrderResponse
}

const fetchListTradingSecuritiesEpic: Epic<Action> = (action$, state$) =>
  action$.pipe(
    ofType('listTrading.fetch'),
    switchMap((action: ListTradingSecuritiesFetchAction) => {
      const getListTradingSecurities$ = getHub().stream<
        ListTradingSecurityResponse[]
      >('GetListTradingSecurities', action.payload)
      return getListTradingSecurities$.pipe(
        withLatestFrom(state$),
        map(
          ([securitiesList, state]: [ListTradingSecurityResponse[], State]) => {
            const watchlist = getWatchlistDetails(state)(action.payload)
            const allowedIds = watchlist?.securityIds
            const filtered = allowedIds?.length
              ? securitiesList.filter((s) => allowedIds.includes(s.id))
              : securitiesList
            return listTradingSecuritiesFetchSuccess(
              filtered.map((security) => {
                const bestOrder = security.bestOrder
                  ? createOrderFromResponse(security.bestOrder)
                  : undefined
                return {
                  ...security,
                  bestOrder
                }
              })
            )
          }
        ),
        takeUntil(action$.pipe(ofType('listTrading.fetchFail'))),
        catchError((err) =>
          of(listTradingSecuritiesFetchFail(err), logError(err))
        )
      )
    })
  )

const saveOrderEpic: Epic = (action$) =>
  action$.pipe(
    ofType('listTrading.createWorkingListTradingOrder'),
    mergeMap((action: CreateWorkingListTradingOrderAction) => {
      const { watchlistId, ...order } = action.payload
      return of(updateListTradingList(watchlistId, [order]))
    })
  )

type SaveNewRowsStateSlice = Pick<State, 'watchList' | 'webSettings'>
const saveNewListTradingRowsEpic: Epic<
  Action,
  Action,
  SaveNewRowsStateSlice
> = (action$, state$) =>
  action$.pipe(
    ofType<SaveNewListTradingRowsAction>('listTrading.saveNewListTradingRows'),
    switchMap((action) => {
      const rows = action.payload.rows.map((lto) => ({
        ...lto,
        cusipOrIsin: lto.cusipOrIsin.toUpperCase()
      }))
      const isins = rows.map((row) => row.cusipOrIsin)
      return getSecurityIdsFromIdentifier$(isins).pipe(
        mergeMap((secIds) => {
          const resolutions = buildResolutions(isins, secIds)

          if (!allResolved(resolutions)) {
            return of(validateIdentifiersComplete(resolutions, false))
          }

          const watchlist = getWatchlistDetails(state$.value)(
            action.payload.watchlistId
          )
          const existingIds = watchlist?.securityIds ?? []
          const newSecIds =
            existingIds.length > 0
              ? secIds.flat().filter((id) => !existingIds.includes(id))
              : secIds.flat()

          const maxRows = getListTradingMaxRows(state$.value)
          const extra = newSecIds.length + existingIds.length - maxRows
          if (extra > 0) {
            return of(
              createListTradingListFailure(
                `Max List of ${maxRows} line items supported. Please remove ${extra} items to proceed.`
              )
            )
          }
          return of(addListTradingRows(action.payload.watchlistId, rows))
        }),
        catchError((err) => of(logError(err)))
      )
    })
  )

const deleteListTradingSecuritiesEpic: Epic = (action$) =>
  action$.pipe(
    ofType('listTrading.deleteSecurities'),
    mergeMap((action: DeleteListTradingSecuritiesAction) => {
      const { listId, securityIds } = action.payload
      return from(securityIds).pipe(
        mergeMap((securityId) =>
          getHub()
            .invoke('DeleteListTradingSecurity', listId, securityId)
            .pipe(
              map(() => null),
              catchError((err) => of(logError(err)))
            )
        ),
        toArray(),
        mergeMap((results) => {
          const errors: Action[] = results.filter(
            (r): r is ReturnType<typeof logError> => r !== null
          )
          return from([
            ...errors,
            listTradingSecuritiesFetch(listId),
            fetchListTradingSecurities(listId, 0)
          ])
        })
      )
    })
  )

type TradeSelectedSecuritiesState = Pick<
  State,
  'listTrading' | 'depthofmarket' | 'userPreferences' | 'webSettings'
>
const tradeSelectedSecurities: Epic<
  Action,
  Action,
  TradeSelectedSecuritiesState
> = (action$, state$) =>
  action$.pipe(
    ofType('listTrading.tradeSelectedSecurities'),
    mergeMap((action: TradeSelectedSecuritiesAction) => {
      const selectedSecurities = getSelectedSecurities(state$.value)
      const actions: SubmitOrderAction[] = []
      /*
         Selected ids resolve from their GetOrderById watches (echoed on
         subscribe, seeded for accumulated bests); the live bestOrder wins any
         tie. The DOM store isn't consulted: a closed ladder leaves it frozen.
      */
      const passiveOrders: Record<string, Order> = {
        ...getWatchedOrders(state$.value)
      }
      selectedSecurities.forEach((security) => {
        if (security.bestOrder) {
          passiveOrders[security.bestOrder.id] = security.bestOrder
        }
      })
      const userPreferences = getUserPreferences(state$.value)
      const enableLTHedge = getEnableLTHedge(state$.value)
      const spotCrossSelection = !enableLTHedge
        ? 'S'
        : userPreferences.defaultSpotHedgePreference || 'S'

      selectedSecurities.forEach((security) => {
        const workingOrder = getWorkingOrderFromStateBySecurityId(
          state$.value,
          security.id
        )

        let interest = security.remainingInterest

        /*
           Allocate My Size best-first by live price, not selection order: an
           older selected order can be repriced to best again after accumulating.
        */
        const trades = workingOrder.selectedOrders
          .map((orderId) => passiveOrders[orderId])
          .filter((trade): trade is Order => !!trade)
          .sort(compareBestFirst(!!security.isBid, !!security.targetSpread))

        trades.forEach((trade) => {
          const tradeSize = Math.min(interest || 0, trade.size)
          if (!tradeSize) return
          interest = (interest || 0) - tradeSize
          actions.push(
            submitOrder(
              trade,
              trade.id,
              tradeSize,
              -99,
              0,
              spotCrossSelection,
              action.payload
            )
          )
        })
      })
      if (!actions.length) return EMPTY
      return from([...actions])
    })
  )

type CancelLtOrdersState = Pick<State, 'listTrading' | 'order'>
const cancelListTradingOrders: Epic<Action, Action, CancelLtOrdersState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType('listTrading.cancelListTradingOrders'),
    switchMap((action: CancelListTradingOrdersAction) => {
      const selectedOrders: Array<Order['id']> = []
      const workingOrders = getWorkingOrders(state$.value)
      const getAggressOrders = getListTradeOrders(state$.value)
      /* eslint-disable guard-for-in */
      for (const securityId in workingOrders) {
        const workingOrder = workingOrders[securityId]
        if (!workingOrder) {
          continue
        }
        if (workingOrder.ordersToCancel.length) {
          const aggressOrders = getAggressOrders(
            Number(securityId),
            workingOrder.isBid ? 'buy' : 'sell',
            action.payload.watchlistId
          ).filter(
            (order) =>
              isPending(order) && workingOrder.ordersToCancel.includes(order.id)
          )
          selectedOrders.push(...aggressOrders.map((o) => o.id))
        }
      }
      if (!selectedOrders.length) {
        return EMPTY
      }
      return of(
        cancelOrders(selectedOrders),
        addLogItem(
          `Cancelling ${selectedOrders.length} orders from list trading`
        )
      )
    })
  )

const deselectCancelledOrders: Epic<
  Action,
  Action,
  Pick<State, 'listTrading'>
> = (action$, state$) =>
  action$.pipe(
    ofType('order.addOrUpdateUserOrders'),
    switchMap((action: AddOrUpdateUserOrdersAction) => {
      const workingOrders = getWorkingOrders(state$.value)
      const actions: ToggleCancelSubOrderSelectionAction[] = []
      action.payload
        .filter((order) => !isPending(order))
        .forEach((order) => {
          if (
            workingOrders[order.securityId]?.ordersToCancel.includes(order.id)
          ) {
            actions.push(
              toggleCancelSubOrderSelection(order.securityId, order.id)
            )
          }
        })
      return from(actions)
    })
  )

const validateListTradingIdentifiersEpic: Epic = (action$) =>
  action$.pipe(
    ofType<ValidateListTradingIdentifiersAction>(
      'listTrading.validateIdentifiers'
    ),
    switchMap((action) => {
      const identifiers = action.payload
      return getSecurityIdsFromIdentifier$(identifiers).pipe(
        mergeMap((secIds) => {
          const resolutions = buildResolutions(identifiers, secIds)
          const valid = allResolved(resolutions)
          const invalidIdentifiers = valid
            ? []
            : identifiers.filter((id) => resolutions[id].length === 0)

          const actions: Action[] = [
            validateIdentifiersComplete(resolutions, valid)
          ]
          if (invalidIdentifiers.length) {
            actions.push(logInvalidIdentifiers(invalidIdentifiers))
          }
          return from(actions)
        }),
        catchError((err) => of(validateIdentifiersFailed(), logError(err)))
      )
    })
  )

/*
   Keep the per-order GetOrderById watches in sync with the selection: subscribe
   accumulated ids not yet watched, unsubscribe watches no longer selected.
   Membership is read from state so re-triggers never double-subscribe.
*/
type SyncWatchedOrdersState = Pick<State, 'listTrading'>
const syncWatchedOrdersEpic: Epic<Action, Action, SyncWatchedOrdersState> = (
  action$,
  state$
) =>
  action$.pipe(
    ofType(
      'listTrading.fetch',
      'listTrading.fetchSuccess',
      'listTrading.selectListTradingSecurities',
      'listTrading.toggleSuborderSelection'
    ),
    mergeMap(() => {
      const workingOrders = getWorkingOrders(state$.value)
      const watchedIds = getWatchedOrderIds(state$.value)
      const desired: Array<{ securityId: number; orderId: string }> = []
      for (const securityId in workingOrders) {
        const ws = workingOrders[securityId]
        if (!ws) {
          continue
        }
        ws.selectedOrders.forEach((orderId) => {
          desired.push({ securityId: Number(securityId), orderId })
        })
      }
      const desiredIds = new Set(desired.map((d) => d.orderId))
      const toWatch = desired
        .filter((d) => !watchedIds.includes(d.orderId))
        .map((d) => watchOrder(d.securityId, d.orderId))
      const toUnwatch = watchedIds
        .filter((id) => !desiredIds.has(id))
        .map((id) => unwatchOrder(id))
      const actions = [...toWatch, ...toUnwatch]
      return actions.length ? from(actions) : EMPTY
    })
  )

export default combineEpics(
  createInvokeEpic(
    'listTrading.addListTradingRows',
    addNewListTradingRowsDefinition
  ),
  createStreamEpic('listTrading.watchOrder', watchOrderDefinition),
  syncWatchedOrdersEpic,
  createInvokeEpic('listTrading.createList', createListTradingListDefinition),
  createInvokeEpic(
    'listTrading.deleteSecurity',
    deleteListTradingSecurityDefinition
  ),
  createInvokeEpic('listTrading.updateList', updateListTradingListDefinition),
  cancelListTradingOrders,
  deleteListTradingSecuritiesEpic,
  deselectCancelledOrders,
  fetchListTradingSecuritiesEpic,
  saveNewListTradingRowsEpic,
  saveOrderEpic,
  tradeSelectedSecurities,
  validateListTradingIdentifiersEpic
)
